import React, { useState, useEffect } from 'react';
import {
  Globe, FileText, ShieldCheck, Search, ChevronLeft,
  ChevronRight, Eye, Download, CheckCircle, XCircle,
  AlertTriangle, TrendingUp, BarChart3, RefreshCw,
  Plus, Edit2, Trash2, Settings, Save, ToggleLeft, ToggleRight,
  ThumbsUp, ThumbsDown, MessageSquare, Paperclip,
  Activity, Building2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { resolveFileUrl } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GRADE_COLORS = {
  A: 'bg-green-100 text-green-800 ring-green-300',
  B: 'bg-blue-100 text-blue-800 ring-blue-300',
  C: 'bg-yellow-100 text-yellow-800 ring-yellow-300',
  D: 'bg-orange-100 text-orange-800 ring-orange-300',
  E: 'bg-red-100 text-red-800 ring-red-300',
};

const GradeBadge = ({ grade, size = 'md' }) => {
  const sizeClass = size === 'lg' ? 'w-10 h-10 text-lg' : 'w-7 h-7 text-sm';
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-bold ring-1 ${GRADE_COLORS[grade] || 'bg-gray-100 text-gray-600'} ${sizeClass}`}>
      {grade || '?'}
    </span>
  );
};

const ComplianceBadge = ({ compliance }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    compliance ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }`}>
    {compliance ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {compliance ? 'Pass' : 'Fail'}
  </span>
);

// ============================================================
// LOI Request Detail Modal
// ============================================================
const LOIDetailModal = ({ requestId, onClose, onStatusUpdate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await api.get(`/requests/${requestId}`);
        setData(res.data);
        setAdminNotes(res.data.request?.admin_notes || '');
      } catch (error) {
        toast.error('Failed to load LOI details');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [requestId, onClose]);

  const handleUpdateStatus = async (status) => {
    if (status === 'denied' && !adminNotes.trim()) {
      toast.error('Please provide admin notes when denying a request');
      return;
    }
    setUpdating(true);
    try {
      await api.put(`/admin/requests/${requestId}`, { status, admin_notes: adminNotes });
      toast.success(`LOI request ${status}`);
      onStatusUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const renderAnswerValue = (answer) => {
    const val = answer.answer_value;
    if (!val || val === '-') return <span className="text-gray-500">-</span>;
    const strVal = String(val);
    if (strVal.startsWith('/api/files/') || ((strVal.startsWith('http://') || strVal.startsWith('https://')) && /\.(jpg|jpeg|png|gif|webp|svg|pdf|doc|docx)$/i.test(strVal))) {
      const fullUrl = resolveFileUrl(strVal, BACKEND_URL);
      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(strVal);
      return isImage ? (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="block">
          <img src={fullUrl} alt="upload" className="max-w-xs max-h-32 rounded border" />
        </a>
      ) : (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
          <Paperclip className="w-3 h-3" /> View File
        </a>
      );
    }
    return <span>{strVal}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              LOI Request Details
            </h2>
            {data?.request && (
              <p className="text-sm text-gray-500 mt-1">
                #{requestId.slice(-8)} &bull; {data.request.company_name || 'Unknown Company'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <XCircle className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
            </div>
          ) : data ? (
            <>
              {/* Request Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Request Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Status:</span>{' '}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      data.request.status === 'approved' ? 'bg-green-100 text-green-700' :
                      data.request.status === 'denied' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>{data.request.status}</span>
                  </div>
                  <div><span className="text-gray-500">Company:</span> <span className="font-medium">{data.request.company_name || '-'}</span></div>
                  <div><span className="text-gray-500">Submitted:</span> <span className="font-medium">{data.request.submitted_at ? new Date(data.request.submitted_at).toLocaleString() : '-'}</span></div>
                  {data.request.reviewed_at && (
                    <div><span className="text-gray-500">Reviewed:</span> <span className="font-medium">{new Date(data.request.reviewed_at).toLocaleString()}</span></div>
                  )}
                  {data.request.email && (
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium">{data.request.email}</span></div>
                  )}
                </div>
              </div>

              {/* Answers */}
              {data.answers && data.answers.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Submitted Answers</h3>
                  <div className="space-y-2">
                    {data.answers.map((a, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">{a.question_text}</p>
                        <div className="text-sm text-gray-900">{renderAnswerValue(a)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {data.documents && data.documents.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Uploaded Documents</h3>
                  <div className="space-y-2">
                    {data.documents.map((doc) => (
                      <div key={doc.id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                        <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                          <p className="text-xs text-gray-400">
                            {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                            {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleString()}` : ''}
                          </p>
                        </div>
                        {doc.file_path && (
                          <a
                            href={`${BACKEND_URL}/api/files/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                          >
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes + Actions */}
              {data.request.status === 'pending' && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Admin Decision</h3>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Admin notes (required for denial)..."
                    className="w-full px-3 py-2 border rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => handleUpdateStatus('denied')}
                      disabled={updating}
                      className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm disabled:opacity-50"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      Deny
                    </button>
                    <button
                      onClick={() => handleUpdateStatus('approved')}
                      disabled={updating}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      Approve
                    </button>
                  </div>
                </div>
              )}

              {/* Existing admin notes */}
              {data.request.admin_notes && data.request.status !== 'pending' && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-800 mb-1 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Admin Notes
                  </h3>
                  <p className="text-sm text-yellow-700">{data.request.admin_notes}</p>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Tab 1: View LOIs (Internal Platform LOIs)
// ============================================================
const ViewLOIsTab = () => {
  const [lois, setLois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLOI, setSelectedLOI] = useState(null);

  useEffect(() => {
    fetchLOIs();
  }, [page, statusFilter]);

  const fetchLOIs = async () => {
    try {
      setLoading(true);
      let url = `/admin/requests?page=${page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      const data = res.data;
      setLois(data.items || data);
      setTotalPages(data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch LOIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLOIs();
  };

  // Count by status
  const pendingCount = lois.filter(l => l.status === 'pending').length;

  return (
    <div>
      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          <p className="text-sm text-yellow-700">
            <strong>{pendingCount}</strong> LOI request(s) pending review
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
          </div>
        ) : lois.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No LOIs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">LOI #</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Creator</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Company</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lois.map((loi) => (
                  <tr key={loi.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{loi.id?.slice(-8)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {loi.submitted_at ? new Date(loi.submitted_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{loi.user_name || loi.user_email || '-'}</p>
                      <p className="text-xs text-gray-400">{loi.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{loi.company_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        loi.status === 'approved' ? 'bg-green-100 text-green-700' :
                        loi.status === 'denied' ? 'bg-red-100 text-red-700' :
                        loi.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {loi.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedLOI(loi.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-white"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-white"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* LOI Detail Modal */}
      {selectedLOI && (
        <LOIDetailModal
          requestId={selectedLOI}
          onClose={() => setSelectedLOI(null)}
          onStatusUpdate={fetchLOIs}
        />
      )}
    </div>
  );
};

// ============================================================
// Tab 2: LOI Verifications (Public + Internal Analysis)
// ============================================================
const LOIVerificationsTab = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    grade: '',
    compliance: '',
    source: '',
    date_from: '',
    date_to: '',
  });
  const [selectedVerification, setSelectedVerification] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 20);
      if (filters.grade) params.set('grade', filters.grade);
      if (filters.compliance) params.set('compliance', filters.compliance);
      if (filters.source) params.set('source', filters.source);
      if (filters.search) params.set('search', filters.search);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);

      const [verRes, statsRes] = await Promise.all([
        api.get(`/admin/loi-verification/verifications?${params.toString()}`),
        api.get('/admin/loi-verification/stats'),
      ]);

      setVerifications(verRes.data.items || []);
      setTotalPages(verRes.data.pages || 1);
      setTotal(verRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const viewDetail = async (id) => {
    try {
      const res = await api.get(`/admin/loi-verification/verifications/${id}`);
      setSelectedVerification(res.data);
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (filters.grade) params.set('grade', filters.grade);
      if (filters.compliance) params.set('compliance', filters.compliance);
      if (filters.source) params.set('source', filters.source);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const res = await api.get(`/admin/loi-verification/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `loi_verifications_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export verifications');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Export Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50"
        >
          <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
          <div className="bg-white p-3 rounded-lg border">
            <p className="text-xs text-gray-500">Total Analyzed</p>
            <p className="text-xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-green-100">
            <p className="text-xs text-green-600">Passed</p>
            <p className="text-xl font-bold text-green-600">{stats.passed}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-red-100">
            <p className="text-xs text-red-600">Failed</p>
            <p className="text-xl font-bold text-red-600">{stats.failed}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-600">Today Total</p>
            <p className="text-xl font-bold text-blue-600">{stats.today_total}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-green-100">
            <p className="text-xs text-green-600">Today Good</p>
            <p className="text-xl font-bold text-green-600">{stats.today_passed}</p>
          </div>
          <div className="bg-white p-3 rounded-lg border border-red-100">
            <p className="text-xs text-red-600">Today Bad</p>
            <p className="text-xl font-bold text-red-600">{stats.today_failed}</p>
          </div>
        </div>
      )}

      {/* Good vs Bad Widget */}
      {stats && stats.total > 0 && (
        <div className="bg-white rounded-lg border p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-gray-400" />
              Compliance Overview
            </h3>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
                <div
                  className="bg-green-500 transition-all"
                  style={{ width: `${(stats.passed / stats.total) * 100}%` }}
                />
                <div
                  className="bg-red-400 transition-all"
                  style={{ width: `${(stats.failed / stats.total) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-bold whitespace-nowrap">
              <span className="text-green-600">{stats.passed}</span>
              <span className="text-gray-400">/</span>
              <span className="text-red-600">{stats.failed}</span>
              <span className="text-gray-400 text-xs font-normal">Good / Bad</span>
            </div>
          </div>
          {/* Grade Distribution */}
          {stats.grade_distribution && Object.keys(stats.grade_distribution).length > 0 && (
            <div className="flex gap-2 mt-3">
              {['A', 'B', 'C', 'D', 'E'].map(g => (
                <div key={g} className="flex items-center gap-1">
                  <GradeBadge grade={g} />
                  <span className="text-xs text-gray-500">{stats.grade_distribution[g] || 0}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, name, filename..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.grade}
            onChange={(e) => { setFilters({ ...filters, grade: e.target.value }); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Grades</option>
            {['A', 'B', 'C', 'D', 'E'].map(g => (
              <option key={g} value={g}>Grade {g}</option>
            ))}
          </select>
          <select
            value={filters.compliance}
            onChange={(e) => { setFilters({ ...filters, compliance: e.target.value }); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Results</option>
            <option value="pass">Passed</option>
            <option value="fail">Failed</option>
          </select>
          <select
            value={filters.source}
            onChange={(e) => { setFilters({ ...filters, source: e.target.value }); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Sources</option>
            <option value="public">Public</option>
            <option value="internal">Internal</option>
          </select>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          />
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
            Search
          </button>
        </form>
      </div>

      {/* Verifications Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
          </div>
        ) : verifications.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No verifications yet</p>
            <p className="text-sm text-gray-400">LOI verifications will appear here when users upload documents.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Grade</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Filename</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Uploader</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Score</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Compliance</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {verifications.map((v) => {
                  const summary = v.result?.summary || {};
                  return (
                    <tr key={v.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <GradeBadge grade={summary.grade} />
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}
                        <br />
                        <span className="text-xs text-gray-400">
                          {v.created_at ? new Date(v.created_at).toLocaleTimeString() : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900 truncate max-w-[200px]">{v.original_filename || '-'}</p>
                        <p className="text-xs text-gray-400">{(v.file_size / 1024).toFixed(0)} KB</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">{v.uploader_name || v.uploader_email || 'Anonymous'}</p>
                        {v.uploader_email && v.uploader_name && (
                          <p className="text-xs text-gray-400">{v.uploader_email}</p>
                        )}
                        {v.company_name && (
                          <p className="text-xs text-blue-500">{v.company_name}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          v.source === 'public' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {v.source}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-gray-900">{summary.score || 0}%</span>
                        <br />
                        <span className="text-xs text-gray-400">
                          {summary.passed || 0}/{summary.total_checks || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ComplianceBadge compliance={summary.compliance} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => viewDetail(v.id)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-500">{total} results</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-white"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-white"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedVerification && (
        <VerificationDetailModal
          verification={selectedVerification}
          onClose={() => setSelectedVerification(null)}
        />
      )}
    </div>
  );
};

// ============================================================
// Verification Detail Modal
// ============================================================
const VerificationDetailModal = ({ verification, onClose }) => {
  const result = verification.result || {};
  const checks = result.checks || [];
  const grading = result.grading || {};
  const meta = result.metadata || {};
  const contact = result.contact_info || {};
  const intel = result.intel_analysis || null;

  const checksByCategory = {};
  checks.forEach(c => {
    if (!checksByCategory[c.category]) checksByCategory[c.category] = [];
    checksByCategory[c.category].push(c);
  });

  const categoryLabels = {
    content: 'Content Fields',
    metadata: 'Metadata',
    revision: 'Revision History',
    stamp: 'Stamps & Images',
    xmp: 'XMP Metadata',
    forensic: 'Forensic',
    consistency: 'Consistency',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-3">
              LOI Verification Report
              <GradeBadge grade={grading.grade} size="lg" />
              <ComplianceBadge compliance={grading.compliance} />
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {verification.original_filename} &bull;
              {verification.uploader_email || 'Anonymous'} &bull;
              {new Date(verification.created_at).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Download original PDF */}
            <a
              href={`${BACKEND_URL}/api/public/loi-verification/report/${verification._id || verification.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <Download className="w-4 h-4" />
              PDF Report
            </a>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <XCircle className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Score Overview */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{grading.score}%</p>
              <p className="text-sm text-gray-500">Overall Score</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600">{grading.level_1?.passed}/{grading.level_1?.total}</p>
              <p className="text-sm text-red-500">Level 1 (Must Have)</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-xl font-bold text-orange-600">{grading.level_2?.passed}/{grading.level_2?.total}</p>
              <p className="text-sm text-orange-500">Level 2 (Important)</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-600">{grading.level_3?.passed}/{grading.level_3?.total}</p>
              <p className="text-sm text-blue-500">Level 3 (Nice to Have)</p>
            </div>
          </div>

          {/* Metadata Overview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Document Info</h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              <div><span className="text-gray-500">Author:</span> <span className="font-medium">{meta.author || 'N/A'}</span></div>
              <div><span className="text-gray-500">Creator:</span> <span className="font-medium">{meta.creator || 'N/A'}</span></div>
              <div><span className="text-gray-500">Producer:</span> <span className="font-medium">{meta.producer || 'N/A'}</span></div>
              <div><span className="text-gray-500">Pages:</span> <span className="font-medium">{meta.page_count}</span></div>
              <div><span className="text-gray-500">Format:</span> <span className="font-medium">{meta.format || 'N/A'}</span></div>
              <div><span className="text-gray-500">File Size:</span> <span className="font-medium">{(verification.file_size / 1024).toFixed(1)} KB</span></div>
            </div>
          </div>

          {/* Contact Info */}
          {(contact.emails?.length > 0 || contact.domains?.length > 0) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Extracted Contact Info</h3>
              <div className="space-y-1 text-sm">
                {contact.emails?.length > 0 && (
                  <div><span className="text-gray-500">Emails:</span> {contact.emails.join(', ')}</div>
                )}
                {contact.domains?.length > 0 && (
                  <div><span className="text-gray-500">Domains:</span> {contact.domains.join(', ')}</div>
                )}
                {contact.phones?.length > 0 && (
                  <div><span className="text-gray-500">Phones:</span> {contact.phones.slice(0, 5).join(', ')}</div>
                )}
              </div>
            </div>
          )}

          {/* Intel AI Analysis */}
          {intel && (
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Trados Intel Analysis
                {intel.model_used && (
                  <a href="https://www.demelos.com" target="_blank" rel="noopener noreferrer" className="text-xs font-normal text-purple-500 ml-auto hover:underline">Powered by demelos.com</a>
                )}
              </h3>

              {/* Overall Assessment */}
              {intel.sections?.overall_assessment && (
                <div className="bg-white rounded-lg p-3 mb-3 text-sm text-gray-700">
                  {intel.sections.overall_assessment}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Red Flags */}
                {intel.sections?.red_flags?.length > 0 && intel.sections.red_flags[0] !== 'No red flags detected.' && (
                  <div>
                    <h4 className="text-xs font-semibold text-red-600 mb-1.5">Red Flags</h4>
                    <div className="space-y-1">
                      {intel.sections.red_flags.map((flag, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs bg-red-50 rounded p-2">
                          <XCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{flag}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Positive Indicators */}
                {intel.sections?.positive_indicators?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-green-600 mb-1.5">Positive Indicators</h4>
                    <div className="space-y-1">
                      {intel.sections.positive_indicators.map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs bg-green-50 rounded p-2">
                          <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {intel.sections?.recommendations?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-blue-600 mb-1.5">Recommendations</h4>
                    <div className="space-y-1">
                      {intel.sections.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-xs bg-blue-50 rounded p-2">
                          <TrendingUp className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Checks by Category */}
          {Object.entries(checksByCategory).map(([category, categoryChecks]) => (
            <div key={category}>
              <h3 className="font-semibold text-gray-900 mb-2">{categoryLabels[category] || category}</h3>
              <div className="space-y-1">
                {categoryChecks.map((check, idx) => (
                  <div
                    key={idx}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      check.passed ? 'bg-green-50' : check.severity === 'critical' ? 'bg-red-50' : 'bg-yellow-50'
                    }`}
                  >
                    {check.passed ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : check.severity === 'critical' ? (
                      <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{check.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{check.detail}</p>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                      check.severity === 'critical' ? 'bg-red-100 text-red-600' :
                      check.severity === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {check.severity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Tab 3: LOI Check Items (Configuration)
// ============================================================
const CHECK_CATEGORIES = [
  { value: 'content', label: 'Content Fields', color: 'bg-blue-100 text-blue-800' },
  { value: 'metadata', label: 'Metadata', color: 'bg-purple-100 text-purple-800' },
  { value: 'revision', label: 'Revision History', color: 'bg-orange-100 text-orange-800' },
  { value: 'stamp', label: 'Stamps & Images', color: 'bg-green-100 text-green-800' },
  { value: 'xmp', label: 'XMP Metadata', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'forensic', label: 'Forensic', color: 'bg-red-100 text-red-800' },
  { value: 'consistency', label: 'Consistency', color: 'bg-yellow-100 text-yellow-800' },
];

const CHECK_LEVELS = [
  { value: 1, label: 'Level 1 — Must Have', color: 'text-red-600 bg-red-50', badge: 'bg-red-500 text-white' },
  { value: 2, label: 'Level 2 — Important', color: 'text-orange-600 bg-orange-50', badge: 'bg-orange-500 text-white' },
  { value: 3, label: 'Level 3 — Nice to Have', color: 'text-blue-600 bg-blue-50', badge: 'bg-blue-500 text-white' },
];

const LOICheckItemsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [threshold, setThreshold] = useState(['A', 'B']);
  const [seeding, setSeeding] = useState(false);

  const [formData, setFormData] = useState({
    check_id: '',
    label: '',
    description: '',
    category: 'content',
    level: 2,
    active: true,
    auto_detected: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, thresholdRes] = await Promise.all([
        api.get('/admin/loi-verification/items'),
        api.get('/admin/loi-verification/compliance-threshold'),
      ]);
      setItems(itemsRes.data);
      setThreshold(thresholdRes.data.passing_grades || ['A', 'B']);
    } catch (error) {
      toast.error('Failed to fetch verification items');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setSeeding(true);
      const res = await api.post('/admin/loi-verification/items/seed-defaults');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed defaults');
    } finally {
      setSeeding(false);
    }
  };

  const handleSaveThreshold = async () => {
    try {
      await api.put('/admin/loi-verification/compliance-threshold', { passing_grades: threshold });
      toast.success('Compliance threshold updated');
    } catch (error) {
      toast.error('Failed to update threshold');
    }
  };

  const toggleGrade = (grade) => {
    setThreshold(prev =>
      prev.includes(grade)
        ? prev.filter(g => g !== grade)
        : [...prev, grade].sort()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/admin/loi-verification/items/${editingItem.id}`, formData);
        toast.success('Item updated');
      } else {
        await api.post('/admin/loi-verification/items', formData);
        toast.success('Item created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this verification item?')) return;
    try {
      await api.delete(`/admin/loi-verification/items/${id}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await api.put(`/admin/loi-verification/items/${item.id}`, { active: !item.active });
      toast.success(`Item ${item.active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle item');
    }
  };

  const handleLevelChange = async (item, newLevel) => {
    try {
      await api.put(`/admin/loi-verification/items/${item.id}`, { level: newLevel });
      toast.success(`Level updated to ${newLevel}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update level');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ check_id: '', label: '', description: '', category: 'content', level: 2, active: true, auto_detected: true });
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      check_id: item.check_id,
      label: item.label,
      description: item.description || '',
      category: item.category,
      level: item.level,
      active: item.active,
      auto_detected: item.auto_detected,
    });
    setShowModal(true);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchTerm ||
      item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.check_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesLevel = !levelFilter || item.level === parseInt(levelFilter);
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const getCategoryInfo = (cat) => CHECK_CATEGORIES.find(c => c.value === cat) || { label: cat, color: 'bg-gray-100 text-gray-800' };
  const getLevelInfo = (lvl) => CHECK_LEVELS.find(l => l.value === lvl) || CHECK_LEVELS[1];

  const totalActive = items.filter(i => i.active).length;
  const level1Count = items.filter(i => i.level === 1 && i.active).length;
  const level2Count = items.filter(i => i.level === 2 && i.active).length;
  const level3Count = items.filter(i => i.level === 3 && i.active).length;

  return (
    <div>
      {/* Header Actions */}
      <div className="flex justify-end gap-2 mb-4">
        <button
          onClick={handleSeedDefaults}
          disabled={seeding}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
          Seed Defaults
        </button>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Check
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-500">Total Checks</p>
          <p className="text-2xl font-bold text-gray-900">{items.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{totalActive}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-red-100">
          <p className="text-sm text-red-500">Level 1 (Must Have)</p>
          <p className="text-2xl font-bold text-red-600">{level1Count}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-orange-100">
          <p className="text-sm text-orange-500">Level 2 (Important)</p>
          <p className="text-2xl font-bold text-orange-600">{level2Count}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-500">Level 3 (Nice to Have)</p>
          <p className="text-2xl font-bold text-blue-600">{level3Count}</p>
        </div>
      </div>

      {/* Compliance Threshold */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">Compliance Threshold</h3>
          </div>
          <button
            onClick={handleSaveThreshold}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">Select which grades pass compliance. LOIs with these grades will trigger admin alerts.</p>
        <div className="flex gap-2">
          {['A', 'B', 'C', 'D', 'E'].map(grade => (
            <button
              key={grade}
              onClick={() => toggleGrade(grade)}
              className={`w-12 h-12 rounded-lg font-bold text-lg transition ${
                threshold.includes(grade)
                  ? 'bg-green-500 text-white ring-2 ring-green-300'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              {grade}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search checks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {CHECK_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Levels</option>
            {CHECK_LEVELS.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No verification items found</p>
          <p className="text-sm text-gray-400 mb-4">Click "Seed Defaults" to load the built-in checks, or add custom ones.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item) => {
            const catInfo = getCategoryInfo(item.category);
            const levelInfo = getLevelInfo(item.level);
            return (
              <div
                key={item.id}
                className={`bg-white rounded-lg border p-4 flex items-center gap-4 transition ${
                  !item.active ? 'opacity-50' : ''
                }`}
              >
                <button
                  onClick={() => handleToggleActive(item)}
                  className="flex-shrink-0"
                  title={item.active ? 'Deactivate' : 'Activate'}
                >
                  {item.active ? (
                    <ToggleRight className="w-8 h-8 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-gray-300" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 text-sm">{item.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catInfo.color}`}>
                      {catInfo.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{item.check_id}</p>
                  {item.description && (
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                  )}
                </div>
                <select
                  value={item.level}
                  onChange={(e) => handleLevelChange(item, parseInt(e.target.value))}
                  className={`px-2 py-1 rounded text-xs font-bold border-0 ${levelInfo.color}`}
                >
                  <option value={1}>Level 1</option>
                  <option value={2}>Level 2</option>
                  <option value={3}>Level 3</option>
                </select>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(item)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold">
                {editingItem ? 'Edit Verification Item' : 'Add Verification Item'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check ID</label>
                <input
                  type="text"
                  value={formData.check_id}
                  onChange={(e) => setFormData({ ...formData, check_id: e.target.value })}
                  placeholder="e.g., content_buyer_info"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!!editingItem}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., Buyer / Applicant Information"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What does this check do?"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CHECK_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CHECK_LEVELS.map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.auto_detected}
                    onChange={(e) => setFormData({ ...formData, auto_detected: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Auto-detected by engine</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Tab 4: Analytics & Trends
// ============================================================
const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/loi-verification/analytics?days=${period}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const { daily_trend, top_failing_checks, company_breakdown, weekly_grades } = analytics;

  // Calculate max for bar scaling
  const maxDaily = Math.max(...daily_trend.map(d => d.total), 1);

  return (
    <div>
      {/* Period Selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Verification Analytics
        </h3>
        <select
          value={period}
          onChange={(e) => setPeriod(parseInt(e.target.value))}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Daily Trend Chart (CSS-based bar chart) */}
      {daily_trend.length > 0 && (
        <div className="bg-white rounded-lg border p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            Daily Verification Volume
          </h4>
          <div className="flex items-end gap-1 h-40 overflow-x-auto pb-6 relative">
            {daily_trend.map((day, idx) => {
              const passedH = (day.passed / maxDaily) * 100;
              const failedH = (day.failed / maxDaily) * 100;
              return (
                <div key={idx} className="flex flex-col items-center flex-shrink-0 group" style={{ minWidth: daily_trend.length > 60 ? '8px' : daily_trend.length > 30 ? '14px' : '24px' }}>
                  <div className="flex flex-col justify-end h-32 w-full gap-0.5">
                    <div
                      className="bg-red-400 rounded-t-sm transition-all"
                      style={{ height: `${failedH}%`, minHeight: day.failed > 0 ? '2px' : 0 }}
                      title={`${day.date}: ${day.failed} failed`}
                    />
                    <div
                      className="bg-green-500 rounded-t-sm transition-all"
                      style={{ height: `${passedH}%`, minHeight: day.passed > 0 ? '2px' : 0 }}
                      title={`${day.date}: ${day.passed} passed`}
                    />
                  </div>
                  {daily_trend.length <= 31 && (
                    <span className="text-[9px] text-gray-400 mt-1 transform rotate-45 origin-left whitespace-nowrap">
                      {day.date.slice(5)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 text-xs text-gray-500 mt-2">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-sm inline-block"></span> Passed</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded-sm inline-block"></span> Failed</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Top Failing Checks */}
        {top_failing_checks.length > 0 && (
          <div className="bg-white rounded-lg border p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Top Failing Checks
            </h4>
            <div className="space-y-2">
              {top_failing_checks.map((check, idx) => {
                const maxFail = top_failing_checks[0]?.fail_count || 1;
                const catInfo = CHECK_CATEGORIES.find(c => c.value === check.category) || { color: 'bg-gray-100 text-gray-800' };
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-5 text-right">{idx + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900 truncate">{check.label}</span>
                        <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${catInfo.color}`}>
                          {check.category}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-400 rounded-full transition-all"
                          style={{ width: `${(check.fail_count / maxFail) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-bold text-red-600 flex-shrink-0">{check.fail_count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekly Grade Distribution */}
        {weekly_grades.length > 0 && (
          <div className="bg-white rounded-lg border p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              Weekly Grade Distribution
            </h4>
            <div className="space-y-2">
              {weekly_grades.map((week, idx) => {
                const total = (week.A || 0) + (week.B || 0) + (week.C || 0) + (week.D || 0) + (week.E || 0);
                if (total === 0) return null;
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{week.week}</span>
                      <span>{total} total</span>
                    </div>
                    <div className="flex h-5 rounded-full overflow-hidden">
                      {['A', 'B', 'C', 'D', 'E'].map(g => {
                        const pct = ((week[g] || 0) / total) * 100;
                        if (pct === 0) return null;
                        const colors = { A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', E: 'bg-red-500' };
                        return (
                          <div
                            key={g}
                            className={`${colors[g]} flex items-center justify-center text-white text-[10px] font-bold`}
                            style={{ width: `${pct}%` }}
                            title={`Grade ${g}: ${week[g]}`}
                          >
                            {pct > 12 ? g : ''}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 text-xs text-gray-500 mt-3">
              {['A', 'B', 'C', 'D', 'E'].map(g => {
                const colors = { A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', E: 'bg-red-500' };
                return (
                  <span key={g} className="flex items-center gap-1">
                    <span className={`w-2.5 h-2.5 ${colors[g]} rounded-sm inline-block`}></span> {g}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Company Breakdown */}
      {company_breakdown.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-400" />
            Company Breakdown (Internal Verifications)
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-500">Company</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Total</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Passed</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Failed</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-500">Avg Score</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-500">Pass Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {company_breakdown.map((co, idx) => {
                  const passRate = co.total > 0 ? ((co.passed / co.total) * 100).toFixed(0) : 0;
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{co.company_name}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{co.total}</td>
                      <td className="px-4 py-2 text-right text-green-600 font-medium">{co.passed}</td>
                      <td className="px-4 py-2 text-right text-red-600 font-medium">{co.failed}</td>
                      <td className="px-4 py-2 text-right">
                        <span className="font-bold">{co.avg_score}%</span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${passRate >= 70 ? 'bg-green-500' : passRate >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${passRate}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{passRate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {daily_trend.length === 0 && top_failing_checks.length === 0 && company_breakdown.length === 0 && (
        <div className="bg-white rounded-lg border p-12 text-center">
          <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No verification data for this period</p>
          <p className="text-sm text-gray-400 mt-1">Analytics will appear once LOIs are verified.</p>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Main Page Component
// ============================================================
const AdminExternalServices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'lois';

  const setTab = (tab) => {
    setSearchParams({ tab });
  };

  return (
    <Sidebar>
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-7 h-7 text-blue-600" />
            External Services
          </h1>
          <p className="text-gray-500 mt-1">Monitor LOIs and LOI verifications across the platform</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b overflow-x-auto">
          <button
            onClick={() => setTab('lois')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === 'lois'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            View LOIs
          </button>
          <button
            onClick={() => setTab('verifications')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === 'verifications'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            LOI Verifications
          </button>
          <button
            onClick={() => setTab('analytics')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === 'analytics'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            Analytics & Trends
          </button>
          <button
            onClick={() => setTab('check-items')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === 'check-items'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4" />
            LOI Check Items
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'lois' && <ViewLOIsTab />}
        {activeTab === 'verifications' && <LOIVerificationsTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'check-items' && <LOICheckItemsTab />}
      </div>
    </Sidebar>
  );
};

export default AdminExternalServices;
